"use client";

// Card de aceite de cookies. Persiste a escolha em localStorage — não há
// endpoint backend pra registrar (decisão documentada no handoff RH/Legal).
// Aparece em todas as páginas até o usuário escolher uma opção.

import { useEffect, useRef, useSyncExternalStore } from "react";
import Link from "next/link";
import { useLangContext } from "../lib/lang-context";
import { withLocale } from "../lib/i18n";

const STORAGE_KEY = "mbm-cookie-consent-v1";
const EVENT = "mbm:cookie-consent";

function subscribe(callback) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT, callback);
  return () => window.removeEventListener(EVENT, callback);
}

function readConsent() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari em modo privado: assume aceito pra não chatear usuário.
    return "storage-blocked";
  }
}

// Server snapshot: assume aceito pra evitar flash do banner durante SSR.
// O banner aparece só após hydration quando o storage real é lido.
function readConsentServer() {
  return "ssr";
}

function CookieIcon({ size = 22 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <path d="M12 2.5a9.5 9.5 0 1 0 9.42 10.62 3.4 3.4 0 0 1-4.04-4.04A3.4 3.4 0 0 1 13.34 5 9.5 9.5 0 0 0 12 2.5Z" />
      <circle cx="8.5" cy="11" r="1" fill="currentColor" />
      <circle cx="12" cy="15.5" r="1" fill="currentColor" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" />
      <circle cx="9.5" cy="16" r="0.6" fill="currentColor" />
    </svg>
  );
}

function CookieCloseIcon({ size = 14 }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={size} height={size}
         fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" focusable="false">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

export default function CookieBanner() {
  const { t, lang } = useLangContext();
  const consent = useSyncExternalStore(subscribe, readConsent, readConsentServer);
  const show = consent === null;
  // De onde o foco veio ao entrar no card. O card é o último elemento do
  // documento: sem devolver o foco, quem chegou nele pelo Tab volta pro <body>
  // no dispensar e recomeça a navegação do topo da página.
  const focoAnterior = useRef(null);

  // `focusin` no documento em vez de relatedTarget no card: o alvo anterior
  // pode vir de dentro de um shadow root e chegar retargetado como null.
  useEffect(() => {
    if (!show) return undefined;
    const onFocusIn = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLElement) || el.closest(".cookie-card")) return;
      focoAnterior.current = el;
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [show]);

  if (!show) return null;

  const decide = (value) => {
    const alvo = focoAnterior.current;
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // sem persistência possível
    }
    window.dispatchEvent(new Event(EVENT));
    if (alvo?.isConnected) alvo.focus({ preventScroll: true });
  };

  // Escape só dentro do card. Um listener no documento roubaria o Escape do
  // drawer e do modal de PDF e gravaria o consentimento sem o usuário escolher.
  const onKeyDown = (e) => {
    if (e.key === "Escape") decide("dismissed");
  };

  return (
    <aside
      className="cookie-card"
      role="dialog"
      aria-live="polite"
      aria-labelledby="cookie-card-title"
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="cookie-card-close"
        aria-label={t("cookies_banner_close") || "Fechar"}
        onClick={() => decide("dismissed")}
      >
        <CookieCloseIcon />
      </button>
      <div className="cookie-card-head">
        <span className="cookie-card-icon"><CookieIcon /></span>
        <div>
          <span className="cookie-card-eyebrow">
            {t("cookies_card_eyebrow") || "Privacidade"}
          </span>
          <h3 id="cookie-card-title" className="cookie-card-title">
            {t("cookies_card_title") ||
              "Usamos cookies para melhorar sua experiência"}
          </h3>
        </div>
      </div>
      <p className="cookie-card-body">
        {t("cookies_card_body") ||
          "Cookies essenciais mantêm o site funcionando; os opcionais nos ajudam a entender como você navega e a melhorar o conteúdo. Saiba mais na nossa "}
        <Link href={withLocale("/politica-de-cookies", lang)}>
          {t("cookies_banner_link") || "Política de Cookies"}
        </Link>
        .
      </p>
      <div className="cookie-card-actions">
        <button
          type="button"
          className="cookie-card-btn cookie-card-btn-ghost"
          onClick={() => decide("essential")}
        >
          {t("cookies_card_essential") || "Apenas essenciais"}
        </button>
        <button
          type="button"
          className="cookie-card-btn cookie-card-btn-primary"
          onClick={() => decide("all")}
        >
          {t("cookies_banner_accept") || "Aceitar todos"}
        </button>
      </div>
    </aside>
  );
}
